export const DUO_DISPLAY = Object.freeze({
    outerWidth: 1398, outerHeight: 2034,
    innerWidth: 2670, innerHeight: 1878, panelWidth: 1270, hingeWidth: 130,
    hingeTangentScale: 1
});
export function normalizeFlexibleDisplay(value) {
    if (!value || typeof value !== 'object')
        return null;
    const ref = value;
    return ref.id === 'duo' && ref.version === 1 && ['outer', 'inner', 'both'].includes(ref.mode)
        ? { id: 'duo', version: 1, mode: ref.mode } : null;
}
/** Two content displays, not three. The inner display has two tracking guides
 * but exactly one footage input and one mask, including the curved bridge. */
export function getDuoDisplays(mode) {
    return [
        ...(mode !== 'inner' ? [{ id: 'outer', screenNumber: 1, label: 'Screen 1 · Outer display',
                width: DUO_DISPLAY.outerWidth, height: DUO_DISPLAY.outerHeight, sourceSlot: 1,
                maskSlot: 'mask', atlasChannel: 'R', trackingNumbers: [1] }] : []),
        ...(mode !== 'outer' ? [{ id: 'inner', screenNumber: 2, label: 'Screen 2 · Inner display',
                width: DUO_DISPLAY.innerWidth, height: DUO_DISPLAY.innerHeight, sourceSlot: mode === 'both' ? 2 : 1,
                maskSlot: mode === 'both' ? 'mask2' : 'mask',
                atlasChannel: mode === 'both' ? 'G' : 'R', trackingNumbers: [2, 3] }] : [])
    ];
}
export function getTemplateInputCount(template) {
    const duo = normalizeFlexibleDisplay(template.flexibleDisplay);
    if (duo)
        return getDuoDisplays(duo.mode).length;
    const profile = template.screenProfile;
    return profile?.id === 'triple-screen' ? 3 : profile?.id === 'dual-screen' ? 2 : 1;
}
export function getDuoInputAspect(template, screen = 1) {
    const duo = normalizeFlexibleDisplay(template.flexibleDisplay);
    if (!duo)
        return null;
    const display = getDuoDisplays(duo.mode).find(item => item.sourceSlot === screen);
    return display ? display.width / display.height : null;
}
export function getDuoInputLabel(template, screen = 1) {
    const duo = normalizeFlexibleDisplay(template.flexibleDisplay);
    if (!duo)
        return null;
    return getDuoDisplays(duo.mode).find(item => item.sourceSlot === screen)?.label || null;
}
export function getDuoProfiles(mode) {
    return {
        screenProfile: { id: mode === 'both' ? 'triple-screen' : mode === 'inner' ? 'dual-screen' : 'single-screen', version: 1 },
        staticProfile: { id: mode === 'both' ? 'static.duo-both' : mode === 'inner' ? 'static.duo-inner' : 'static.standard', version: 1 },
        motionProfile: { id: mode === 'both' ? 'motion.duo-both' : mode === 'inner' ? 'motion.duo-inner' : 'motion.single-screen', version: 1 }
    };
}
export function validateFlexibleDisplayTemplate(template) {
    const duo = normalizeFlexibleDisplay(template.flexibleDisplay);
    const staticId = template.staticProfile?.id || '';
    const motionId = template.motionProfile?.id || '';
    if (!duo) {
        return template.flexibleDisplay != null || staticId.startsWith('static.duo-') || motionId.startsWith('motion.duo-')
            ? ['A valid Duo display configuration is required.'] : [];
    }
    const expected = getDuoProfiles(duo.mode);
    const screenId = template.screenProfile?.id;
    if (screenId !== expected.screenProfile.id)
        return ['Duo display mode and geometry profile do not match.'];
    if (template.isVideo && motionId !== expected.motionProfile.id)
        return ['Duo display mode and Motion profile do not match.'];
    if (!template.isVideo && (duo.mode !== 'outer' ? staticId !== expected.staticProfile.id : !['static.standard', 'static.screen-blur', 'static.surface-reflection'].includes(staticId))) {
        return ['Duo display mode and Static profile do not match.'];
    }
    return [];
}
/** Tracking numbers 2 and 3 describe halves of display 2, never extra content
 * screens. Storage slots stay contiguous for existing tracking documents. */
export function getDuoGeometrySlots(mode) {
    if (mode === 'outer')
        return [{ slot: 1, trackingNumber: 1, screenNumber: 1, label: 'Outer display', group: 'outer' }];
    return [
        ...(mode === 'both' ? [{ slot: 1, trackingNumber: 1, screenNumber: 1, label: 'Outer display', group: 'outer' }] : []),
        { slot: mode === 'both' ? 2 : 1, trackingNumber: 2, screenNumber: 2, label: 'Inner display · Left half', group: 'inner' },
        { slot: mode === 'both' ? 3 : 2, trackingNumber: 3, screenNumber: 2, label: 'Inner display · Right half', group: 'inner' }
    ];
}
export function getDuoGeometryLabel(mode, slot, motion = false) {
    const guide = getDuoGeometrySlots(mode).find(item => item.slot === slot);
    return guide ? `${motion ? 'Tracking' : 'Quad guide'} ${guide.trackingNumber} · ${guide.label}` : `Guide ${slot}`;
}
export function validateDuoGeometry(mode, quads) {
    return getDuoGeometrySlots(mode).every(({ slot }) => {
        const points = quads[slot - 1];
        return Array.isArray(points) && points.length === 4 && points.every(p => p && typeof p.x === 'number' && typeof p.y === 'number' && Number.isFinite(p.x) && Number.isFinite(p.y));
    });
}
function projectiveSurface(points) {
    if (points.length !== 4 || points.some(p => !Number.isFinite(p.x) || !Number.isFinite(p.y))) {
        throw new Error('Duo surface needs four finite points in TL, TR, BR, BL order.');
    }
    const q = points.map(p => ({ ...p }));
    const [p0, p1, p2, p3] = q;
    const dx1 = p1.x - p2.x, dy1 = p1.y - p2.y;
    const dx2 = p3.x - p2.x, dy2 = p3.y - p2.y;
    const sx = p0.x - p1.x + p2.x - p3.x, sy = p0.y - p1.y + p2.y - p3.y;
    const determinant = dx1 * dy2 - dx2 * dy1;
    const scale = Math.max(1, ...q.flatMap(p => [Math.abs(p.x - p0.x), Math.abs(p.y - p0.y)]));
    const g = Math.abs(determinant) > scale * scale * 1e-10 ? (sx * dy2 - dx2 * sy) / determinant : NaN;
    const h = Math.abs(determinant) > scale * scale * 1e-10 ? (dx1 * sy - sx * dy1) / determinant : NaN;
    const weights = [1, 1 + g, 1 + g + h, 1 + h];
    const valid = weights.every(w => Number.isFinite(w) && w > 1e-7);
    const a = p1.x - p0.x + g * p1.x, b = p3.x - p0.x + h * p3.x;
    const d = p1.y - p0.y + g * p1.y, e = p3.y - p0.y + h * p3.y;
    const point = (u, v) => valid
        ? { x: (a * u + b * v + p0.x) / (1 + g * u + h * v), y: (d * u + e * v + p0.y) / (1 + g * u + h * v) }
        : {
            x: (1 - v) * (p0.x + u * (p1.x - p0.x)) + v * (p3.x + u * (p2.x - p3.x)),
            y: (1 - v) * (p0.y + u * (p1.y - p0.y)) + v * (p3.y + u * (p2.y - p3.y))
        };
    const tangent = (u, v) => {
        if (!valid)
            return { x: (1 - v) * (p1.x - p0.x) + v * (p2.x - p3.x), y: (1 - v) * (p1.y - p0.y) + v * (p2.y - p3.y) };
        const w = 1 + g * u + h * v;
        return { x: (a * w - g * (a * u + b * v + p0.x)) / (w * w), y: (d * w - g * (d * u + e * v + p0.y)) / (w * w) };
    };
    return { point, tangent };
}
/** Projective panels joined by a cubic Hermite surface. The derivative scale
 * comes from source pixels (130/1270), with the neutral tangent factor of 1.
 * This preserves edge positions and texture-space first derivatives (C1). */
export function createDuoSurface(leftQuad, rightQuad) {
    const left = projectiveSurface(leftQuad), right = projectiveSurface(rightQuad);
    const panel = DUO_DISPLAY.panelWidth, hinge = DUO_DISPLAY.hingeWidth, width = DUO_DISPLAY.innerWidth;
    return (u, v) => {
        const x = Math.max(0, Math.min(1, u)) * width;
        if (x <= panel)
            return left.point(x / panel, v);
        if (x >= panel + hinge)
            return right.point((x - panel - hinge) / panel, v);
        const t = (x - panel) / hinge, t2 = t * t, t3 = t2 * t;
        const l = left.point(1, v), r = right.point(0, v);
        const dl = left.tangent(1, v), dr = right.tangent(0, v);
        const h00 = 2 * t3 - 3 * t2 + 1, h10 = t3 - 2 * t2 + t;
        const h01 = -2 * t3 + 3 * t2, h11 = t3 - t2;
        const scale = (hinge / panel) * DUO_DISPLAY.hingeTangentScale;
        return { x: h00 * l.x + h10 * dl.x * scale + h01 * r.x + h11 * dr.x * scale,
            y: h00 * l.y + h10 * dl.y * scale + h01 * r.y + h11 * dr.y * scale };
    };
}
